"use client"

import * as React from "react"
import { XIcon } from "lucide-react"
import { motion } from "motion/react"

import { cn } from "@/lib/utils"
import { useIsMobile } from "@/hooks/use-mobile"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer"

/**
 * ResponsiveModal — padrão único da plataforma:
 *   • Desktop (>=768px): renderiza um Dialog (modal centralizado).
 *   • Mobile  (<768px):  renderiza um Drawer (vaul, de baixo p/ cima).
 *
 * A API espelha a do Dialog para migração mecânica. Use:
 *   <ResponsiveModal open={open} onOpenChange={setOpen}>
 *     <ResponsiveModalContent>
 *       <ResponsiveModalHeader>
 *         <ResponsiveModalTitle>Título</ResponsiveModalTitle>
 *         <ResponsiveModalDescription>...</ResponsiveModalDescription>
 *       </ResponsiveModalHeader>
 *       ...conteúdo...
 *       <ResponsiveModalFooter>...</ResponsiveModalFooter>
 *     </ResponsiveModalContent>
 *   </ResponsiveModal>
 */

const ResponsiveModalContext = React.createContext<{ isMobile: boolean }>({
  isMobile: false,
})
const useResponsiveModal = () => React.useContext(ResponsiveModalContext)

interface ResponsiveModalProps {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  children: React.ReactNode
}

function ResponsiveModal({ children, ...props }: ResponsiveModalProps) {
  const isMobile = useIsMobile()
  return (
    <ResponsiveModalContext.Provider value={{ isMobile }}>
      {isMobile ? (
        <Drawer {...props}>{children}</Drawer>
      ) : (
        <Dialog {...props}>{children}</Dialog>
      )}
    </ResponsiveModalContext.Provider>
  )
}

function ResponsiveModalTrigger({
  children,
  ...props
}: React.ComponentProps<"button">) {
  const { isMobile } = useResponsiveModal()
  const Trigger = isMobile ? DrawerTrigger : DialogTrigger
  return <Trigger {...(props as any)}>{children}</Trigger>
}

function ResponsiveModalContent({
  className,
  children,
  showCloseButton = true,
}: {
  className?: string
  children: React.ReactNode
  showCloseButton?: boolean
}) {
  const { isMobile } = useResponsiveModal()

  if (isMobile) {
    return (
      <DrawerContent className={cn("max-h-[90dvh]", className)}>
        {showCloseButton && (
          <DrawerClose asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              className="absolute top-3 right-3 z-10"
            >
              <XIcon />
              <span className="sr-only">Close</span>
            </Button>
          </DrawerClose>
        )}
        <div className="flex flex-col flex-1 min-h-0 overflow-y-auto px-4 pt-2 pb-6">
          {children}
        </div>
      </DrawerContent>
    )
  }

  return (
    <DialogContent
      showCloseButton={showCloseButton}
      className={cn("sm:max-w-lg", className)}
    >
      {/* layout animates the modal smoothly when its content changes size.
          grid gap-4 preserves the spacing DialogContent used to provide between
          header / body / footer (otherwise they collapse together).
          max-h + overflow keeps tall content (e.g. a long AI-expanded description)
          scrolling INSIDE the modal instead of overflowing the whole page. */}
      <motion.div layout transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }} className="grid max-h-[80vh] gap-4 overflow-y-auto overflow-x-hidden">
        {children}
      </motion.div>
    </DialogContent>
  )
}

function ResponsiveModalHeader({ className, ...props }: React.ComponentProps<"div">) {
  const { isMobile } = useResponsiveModal()
  const Header = isMobile ? DrawerHeader : DialogHeader
  // DrawerHeader já tem padding próprio; o body do drawer também — neutraliza
  return (
    <Header
      className={cn(isMobile ? "px-0 pt-0 text-left" : "", className)}
      {...props}
    />
  )
}

function ResponsiveModalFooter({ className, ...props }: React.ComponentProps<"div">) {
  const { isMobile } = useResponsiveModal()
  const Footer = isMobile ? DrawerFooter : DialogFooter
  return <Footer className={cn(isMobile ? "px-0 pb-0" : "", className)} {...props} />
}

function ResponsiveModalTitle({ className, ...props }: React.ComponentProps<"h2">) {
  const { isMobile } = useResponsiveModal()
  const Title = isMobile ? DrawerTitle : DialogTitle
  return <Title className={className as any} {...(props as any)} />
}

function ResponsiveModalDescription({
  className,
  ...props
}: React.ComponentProps<"p">) {
  const { isMobile } = useResponsiveModal()
  const Description = isMobile ? DrawerDescription : DialogDescription
  return <Description className={className as any} {...(props as any)} />
}

function ResponsiveModalClose({ children, ...props }: React.ComponentProps<"button">) {
  const { isMobile } = useResponsiveModal()
  const Close = isMobile ? DrawerClose : DialogClose
  return <Close {...(props as any)}>{children}</Close>
}

export {
  ResponsiveModal,
  ResponsiveModalTrigger,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
  ResponsiveModalClose,
}
